import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import displayDuration from '@sdk/utils/displayDuration';
import { TimesheetEntry } from '@sdk/timesheets/types/TimesheetEntry';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { ClockInTime } from '@sdk/timeClock/utils/getActualClockInTimes';
import { BreakAttestation } from '@sdk/automatedBreaks/types/Attestation';

import AutomatedBreaksSummary from '@web/timeClock/modals/rows/ActualRow/AutomatedBreaksSummary';
import BreakDuration from '@web/timeClock/modals/rows/ActualRow/BreakDuration';
import { getTimesToDisplay } from '@web/timeClock/modals/rows/ActualRow/clockInSummary.utils';

type Props = {
  timesheetEntries?: TimesheetEntry[];
  timesheetEntriesBefore?: TimesheetEntry[];
  breakAttestations: BreakAttestation[];
  clockInTime?: ClockInTime;
  clockInTimeBefore?: ClockInTime;
  usTimeFormat: boolean;
  isLast?: boolean;
  areAutomatedBreaksUsed: boolean;
  worldClockEnabled: boolean;
};
const ClockInSummary = ({
  timesheetEntries,
  timesheetEntriesBefore,
  breakAttestations,
  clockInTime,
  clockInTimeBefore,
  usTimeFormat,
  isLast,
  areAutomatedBreaksUsed,
  worldClockEnabled,
}: Props) => {
  let startTimeToDisplay = '';
  let endTimeToDisplay = '';
  let breakMinutes = 0;
  let durationInMinutes = 0;

  if (clockInTime) {
    ({ startTimeToDisplay, endTimeToDisplay } = getTimesToDisplay({
      worldClockEnabled,
      usTimeFormat,
      clockInTime,
    }));
    ({ breakMinutes, durationInMinutes } = clockInTime);
  }

  let startTimeToDisplayBefore = '';
  let endTimeToDisplayBefore = '';
  let breakMinutesBefore = 0;
  let durationInMinutesBefore = 0;

  if (clockInTimeBefore) {
    ({ startTimeToDisplay: startTimeToDisplayBefore, endTimeToDisplay: endTimeToDisplayBefore } =
      getTimesToDisplay({
        worldClockEnabled,
        usTimeFormat,
        clockInTime: clockInTimeBefore,
      }));
    ({ breakMinutes: breakMinutesBefore, durationInMinutes: durationInMinutesBefore } =
      clockInTimeBefore);
  }

  return (
    <Column mBottom={isLast ? 0 : 5}>
      {clockInTimeBefore ? (
        <Row>
          <Text14 themeColor={ThemeColorType.iconGray} strikethrough={true}>
            {startTimeToDisplayBefore} - {endTimeToDisplayBefore}
            {clockInTimeBefore.isInProgress ? null : (
              <> · {displayDuration(durationInMinutesBefore)}</>
            )}
          </Text14>
        </Row>
      ) : null}
      {clockInTime ? (
        <Row>
          <Text14 themeColor={ThemeColorType.gray}>
            {startTimeToDisplay} - {endTimeToDisplay} · {displayDuration(durationInMinutes)}
          </Text14>
        </Row>
      ) : null}
      {clockInTimeBefore ? (
        <>
          {!areAutomatedBreaksUsed && (
            <BreakDuration breakMinutes={breakMinutesBefore} isBefore={true} />
          )}
          {areAutomatedBreaksUsed && (
            <AutomatedBreaksSummary
              timesheetEntries={timesheetEntriesBefore}
              clockInTime={clockInTimeBefore}
              breakAttestations={breakAttestations}
              isBefore={true}
            />
          )}
        </>
      ) : null}
      {clockInTime ? (
        <>
          {!areAutomatedBreaksUsed && <BreakDuration breakMinutes={breakMinutes} />}
          {areAutomatedBreaksUsed && (
            <AutomatedBreaksSummary
              timesheetEntries={timesheetEntries}
              clockInTime={clockInTime}
              breakAttestations={breakAttestations}
            />
          )}
        </>
      ) : null}
    </Column>
  );
};

export default ClockInSummary;
